import React, {useEffect, useState} from "react";
import Files from 'react-files';
import Page from "../../Components/Page";
import {useServiceBase} from "../../Components/ReactTable/useServiceBase";
import {LoadingWrapper} from "../../Components/LoadingWrapper";
import EditableReactTableV2 from "../../Components/ReactTable/EditableReactTableV2";
import {
    EditFormDropdownInputField,
    EditFormNumberInputField,
    EditFormTextInputField
} from "../../Components/ReactTable/EditRecordForm";
import {useParams} from "react-router-dom";
import {makeApiCallPost} from "../../../Api/makeApiCall";
import {toastError} from "../../../utils/toast";
import {toast} from "react-toastify";
import {Label} from "react-foundation";
import {Breadcrumbs} from "../../Components/Breadcrumbs";
import {getStringParamOrDefault} from "../../../utils/urlBuilder";
import {getCellForService, YES_NO_DROPDOWN_OPTIONS} from "../../Components/ReactTable/common_dropdown_options";
import CsvDownload from "../../../utils/CsvDownloadWithNulls";
import _ from "lodash";

function BrandOnboardingManagement() {
    const params = useParams();
    const brandId = parseInt(params.brandId);
    const brandName = getStringParamOrDefault('brandName');

    const endpointUrl = `${process.env.REACT_APP_EDP_API_BASE_URL}/brandManagement/` + brandId;
    const endpointDeleteUrl = `${process.env.REACT_APP_EDP_API_BASE_URL}/brandManagement/delete/` + brandId;
    const {loading, data, setLoading, refreshData} = useServiceBase({endpointUrl});
    const [loadingCSV, setLoadingCSV] = useState(false)
    const [modifiedData, setModifiedData] = useState(false)

    function handlePostResponse(response, shouldThrowError = false) {
        if (response?.apiError) {
            toastError(toast, `Operation failed with api error "${JSON.stringify(response.apiError.message)}"`)
            setLoading(false)
            if (shouldThrowError) {
                throw new Error(`API request failed with API error "${JSON.stringify(response.apiError.message)}"`);
            }
        } else {
            refreshData()
        }
    }

    const onRowEditConfirmedWrapper = async (data) => {
        if(data.hasOwnProperty("index")) {
            delete data.index;
        }

        const dataToSend = [data]
        await makeApiCallPost(endpointUrl, dataToSend)
            .then((data) => handlePostResponse(data, true));
    }

    const onRowDeleteConfirmedWrapper = async (data) => {
        const dataToSend = [data].map(data => data.asin)
        await makeApiCallPost(endpointDeleteUrl, dataToSend)
            .then((data) => handlePostResponse(data));
    }

    useEffect(() => {
        if (_.isEmpty(data)) return;

        setLoading(true)
        const modifiedData = data.map(item => ({...item, enforcement_price: item.enforcement_price.toFixed(2)}));
        setModifiedData(modifiedData)
        setLoading(false)
    }, [data])

    const customColumns = [
        {header: "ASIN", accessorKey: "asin"},
        {header: "UPC", accessorKey: "upc"},
        {header: "Enforcement Price", accessorKey: "enforcement_price"},
        {header: "Pack Size", accessorKey: "pack_size"},
        {
            header: "Active",
            accessorKey: "will_scrape",
            cell: ({getValue}) => getCellForService(getValue(), YES_NO_DROPDOWN_OPTIONS)
        }
    ];

    function uploadCsvPostCall(fileContents) {
        return makeApiCallPost(endpointUrl, JSON.stringify(fileContents));
    }

    function csvUploadError(errors, errorMessage) {
        if (errors.length > 0) {
            toastError(toast,errorMessage);
            setLoadingCSV(false);
            return true;
        }
        return false;
    }

    function transformData(data) {
        return data.map(item => ({
            "ASIN": item.asin,
            "UPC": item.upc,
            "Enforcement Price": item.enforcement_price,
            "Pack Size": item.pack_size,
            "Active": item.will_scrape === 1 ? "Yes" : "No"
        }));
    }

    function downloadCsv() {
        if (!loading)
            CsvDownload(transformData(data), `${brandName}_${brandId}_brand_management.csv`)
    }

    function onFilesChange(files) {
        const requiredHeaders = ["asin", "upc", "enforcement_price", "pack_size"];
        const fileReader = new FileReader();
        const file = files[0];

        fileReader.onload = (loadedFiles) => {
            const csvText = loadedFiles.target.result;

            const lines = csvText.trim().split('\r\n');
            if (lines.length <= 1) {
                toastError(toast,"CSV File is invalid");
                setLoadingCSV(false);
                return;
            }

            const headers = lines[0].toLowerCase().split(',');

            const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
            if (csvUploadError(missingHeaders, "Missing required headers: " + missingHeaders.join(', '))) {
                return;
            }

            const unRecognizedHeaders = headers.filter(h => !requiredHeaders.includes(h));
            if (csvUploadError(unRecognizedHeaders, "Unrecognized headers: " + unRecognizedHeaders.join(', '))) {
                return
            }

            const sanitizedRecords = lines.slice(1).map((line) => {
                const values = line.split(",").map(v => v.trim());
                const jsonRecord = {};
                const integerFields = ['pack_size'];
                const floatFields = ['enforcement_price'];
                headers.forEach((key, i) => {
                    let value = values[i];
                    if (integerFields.includes(key)) {
                        const num = parseFloat(value);
                        jsonRecord[key] = isNaN(num) ? null : num;
                    } else if (floatFields.includes(key)) {
                        const num = parseFloat(value);
                        jsonRecord[key] = isNaN(num) ? null : num.toFixed(2);
                    } else {
                        jsonRecord[key] = value;
                    }
                });
                jsonRecord.will_scrape = 1;

                return jsonRecord;
            });

            const missingRequiredValues = sanitizedRecords.filter(record => (!record.pack_size || !record.enforcement_price || !record.asin));
            if (csvUploadError(missingRequiredValues, `Missing Required Fields: ${missingRequiredValues.length} records missing required fields`)) {
                return
            }

            const packSizeNotInteger = sanitizedRecords.filter(record => record.pack_size !== parseInt(record.pack_size));
            if (csvUploadError(packSizeNotInteger, `Pack Size must be integer: ${packSizeNotInteger.map(record => record.asin).join(', ')}`)) {
                return
            }

            return uploadCsvPostCall(sanitizedRecords)
                .then(() => {
                    setLoadingCSV(false);
                    refreshData()
                    return Promise.resolve();
                }).catch(error => {
                    console.log("=============> error: ", error)
                });
        };
        setLoadingCSV(true);
        fileReader.readAsText(file);
    }

    const validatePreSave = ({form}) => {
        const errors = [
            {field: "pack_size", regex: new RegExp(/^\d+$/), message: 'Must be an integer'},
            {field: "enforcement_price", regex: new RegExp(/^\d+\.\d{2}$/), message: 'Must be an float'}
        ].filter(e => !e.regex.test(form.getValues('pack_size')))
         .filter(e => !e.regex.test(form.getValues('enforcement_price')))

        errors.forEach(e => form.setError(e.field, {type: 'custom', message: [e.message]}))

        return errors.length === 0;
    };

    return (
        <Page name={`Brand Management: ${brandName} - (${brandId})`}
              breadCrumbs={<Breadcrumbs items={[{label: "Brand Onboarding", to: '/brandOnboarding'}, {label: "Brand Management"}]}/>}
        >
            <div className={'right-things'}>
                <div className={'brand-onboarding-management-csv-upload-container'}>
                    <Label className={"csv-label"} title={"Only Retailer Ids"}>
                        <Files
                            className='hollow button secondary files-dropzone'
                            onChange={onFilesChange}
                            onError={(error) => toastError(toast, `Error uploading CSV: error code: ${error.code}:  ${error.message}`)}
                            accepts={['.csv']}
                            multiple
                            maxFiles={1}
                            maxFileSize={10000000}
                            clickable
                        >
                            Upload CSV
                        </Files>
                    </Label>
                </div>
                <div className='brand-onboarding-management-csv-button-container'>
                    <button
                        className='button'
                        data-testid={'brand-onboarding-management-csv-button'}
                        onClick={() => {
                            downloadCsv()
                        }}
                    >

                        Export Csv
                    </button>
                </div>
            </div>
            <LoadingWrapper loading={loading || loadingCSV}>
                <EditableReactTableV2
                    filterable
                    data={modifiedData}
                    columns={customColumns}
                    initialState={{pagination: {pageSize: 25}, sorting: [{id: "asin", desc: false}]}}
                    editTable={[
                        {id: "asin", Component: args => <EditFormTextInputField  {...args} isRequired />},
                        {id: "upc", Component: args => <EditFormTextInputField  {...args} />},
                        {id: "enforcement_price", Component: args => <EditFormTextInputField {...args} isRequired />},
                        {id: "pack_size", Component: args => <EditFormNumberInputField  {...args} isRequired/>},
                        {id: 'will_scrape', defaultValue: 1, Component: args => <EditFormDropdownInputField {...args} options={YES_NO_DROPDOWN_OPTIONS}/>}
                    ]}
                    validatePreSave={validatePreSave}
                    onRowEditConfirmed={onRowEditConfirmedWrapper}
                    onRowDeleteConfirmed={onRowDeleteConfirmedWrapper}
                />
            </LoadingWrapper>
        </Page>);
}

export default BrandOnboardingManagement;
